import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import { MSDocumentEntity, MSGroupEntity } from 'jamespot-user-api';
import { useIntl } from 'react-intl';
import { DriveIsEmpty } from '../components/DriveIsEmpty';
import { EntityItem } from '../components/EntityItem';
import { JRCButtonDropdown } from '../../JRCButtonDropdown/JRCButtonDropdown';
import { JRCLoader } from '../../JRCLoader/JRCLoader';
import { useCallback } from 'react';
import { JRCInputSearchRaw } from '../../Form/Input/JRCInputSearchRaw/JRCInputSearchRaw';
import { JDriveApi } from 'jamespot-user-api/lib/src/apis/drive/drive';
import { useAbortController } from '../../../hooks/useAbortController';

export const CSSSearchAndFilters = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    margin-bottom: ${({ theme }) => theme.space.md}px;
`;

export const CSSSearch = styled.div`
    flex: 1;
    display: flex;
`;

export const LoaderWrapper = styled.div`
    margin-top: ${({ theme }) => theme.space.l}px;
`;

type MSGraphGroupsProps = {
    useFilter: boolean;
    initialFolderColor?: string;
    useNavigation: boolean;
    onSelect?: (entity: MSGroupEntity) => void;
    onClick: (entity: MSGroupEntity) => void;
    onError?: (code: string) => void;
    handlers: {
        drive: Pick<JDriveApi, 'groups'>;
    };
    id: string;
};

type GroupFilterType = '' | 'memberOf';

export const MSGraphGroups = ({
    useFilter,
    useNavigation = true,
    onSelect,
    onClick,
    handlers,
    onError,
    id,
}: MSGraphGroupsProps) => {
    const intl = useIntl();
    const theme = useTheme();
    const abortController = useAbortController();
    const [query, setQuery] = React.useState<string>('');
    const [filter, setFilter] = React.useState<GroupFilterType>('memberOf');
    const [loading, setLoading] = React.useState<boolean>(false);
    const [entities, setEntities] = React.useState<MSDocumentEntity[]>([]);

    const fetchGroups = useCallback(
        (query?: string, filter?: GroupFilterType) => {
            setLoading(true);
            handlers.drive
                .groups({ query, filter, id }, { signal: abortController.signal })
                .then((resp) => {
                    setEntities(resp.result);
                    setLoading(false);
                })
                .catch((error) => {
                    if (onError && !abortController.signal.aborted) {
                        onError(error.errorMsg);
                    }
                })
                .finally(() => {
                    if (!abortController.signal.aborted) {
                        setLoading(false);
                    }
                });
        },
        [abortController.signal, handlers.drive, id, onError],
    );

    React.useEffect(() => {
        fetchGroups(query, filter);
    }, [query, filter, fetchGroups]);

    return (
        <>
            {useFilter && (
                <CSSSearchAndFilters>
                    <CSSSearch>
                        <JRCInputSearchRaw
                            onChange={setQuery}
                            width={'100%'}
                            placeholder={intl.formatMessage({ id: 'DRIVE_Input_Search_Groupe' })}
                        />
                    </CSSSearch>
                    <JRCButtonDropdown
                        small
                        smallOpenDirection="left"
                        options={[
                            {
                                label: intl.formatMessage({ id: 'DRIVE_MS_Group_Filters_All' }),
                                icon: {
                                    name: 'icon-check',
                                    color: filter === '' ? theme.color.green : 'transparent',
                                },
                                onClick: () => setFilter(''),
                            },
                            {
                                label: intl.formatMessage({ id: 'DRIVE_MS_Group_Filters_Member_Of' }),
                                icon: {
                                    name: 'icon-check',
                                    color: filter === 'memberOf' ? theme.color.green : 'transparent',
                                },
                                onClick: () => setFilter('memberOf'),
                            },
                        ]}
                        icon={'icon-ellipsis-v'}
                        label={intl.formatMessage({ id: 'DRIVE_MS_Group_Filters' })}
                        color="primary"
                        variant="outlined"
                    />
                </CSSSearchAndFilters>
            )}
            {loading && (
                <LoaderWrapper>
                    <JRCLoader size={'l'} />
                </LoaderWrapper>
            )}
            {!loading && entities.length === 0 && <DriveIsEmpty />}
            {!loading &&
                entities.map((entity) => (
                    <EntityItem
                        key={entity.id}
                        entity={entity}
                        useNavigation={useNavigation}
                        onSelect={onSelect}
                        onClick={() => onClick(entity)}
                    />
                ))}
        </>
    );
};
